import {
  getCalendarLabelByMode,
  getCalendarTitleByMode,
} from '@modules/common/components/DateRangePicker/CalendarPicker/helpers';
import { DateRangePicker } from '@modules/common/components/DateRangePicker/DateRangePicker';
import { getDateRange } from '@modules/common/components/DateRangePicker/helpers';
import { DateTimeRange } from '@modules/common/components/DateRangePicker/types';
import { formatDateTimeRange } from '@modules/common/utils';
import { useFeedStore } from '@modules/feed/store/feedStore';
import { SearchActionType } from '@modules/feed/store/searchActionType';
import { FeedbackSummaryActionType } from '@modules/oracle/feedbackSummaryStore/feedbackSummaryActionType';
import { useFeedbackSummaryStore } from '@modules/oracle/feedbackSummaryStore/feedbackSummaryStore';
import { Edit } from '@mui/icons-material';
import CalendarTodayRoundedIcon from '@mui/icons-material/CalendarTodayRounded';
import { Backdrop, Button, Stack, Theme, Tooltip } from '@mui/material';
import { useState } from 'react';
import { createAnalyticsTracker } from 'v2/analytics/events';
import { shallow } from 'zustand/shallow';

export const trackSearchV2Events = createAnalyticsTracker('searchV2');

export function DateRangeField() {
  const [isEditMode, setIsEditMode] = useState(false);
  const { duration, dispatch } = useFeedStore(
    ({ query: { duration }, dispatch }) => ({ duration, dispatch }),
    shallow,
  );
  const feedbackSummaryDispatch = useFeedbackSummaryStore(
    ({ dispatch }) => dispatch,
  );

  function onDurationchange(newDuration: DateTimeRange) {
    trackSearchV2Events({
      eventName: 'Search Date Range updated',
      dateRange: formatDateTimeRange(newDuration),
    });
    dispatch({
      type: SearchActionType.SET_QUERY,
      payload: {
        duration: newDuration,
      },
    });
    feedbackSummaryDispatch({
      type: FeedbackSummaryActionType.RESET,
    });

    setIsEditMode(false);
  }

  function renderDate() {
    const shouldShowDatePicker = isEditMode || !duration;

    if (shouldShowDatePicker) {
      return (
        <DateRangePicker
          timeRange={duration}
          onChange={onDurationchange}
          shouldShowQuickLookupOptions
          quickOptionsFilters={{ skipYesterday: true }}
        />
      );
    }

    const title = getCalendarLabelByMode(
      getDateRange(duration),
      duration,
      'Custom',
    );

    const tooltipTitle = getCalendarTitleByMode(
      getDateRange(duration),
      duration.type === 'absolute' ? 'absolute' : 'last',
    );

    return (
      <Tooltip arrow title={tooltipTitle}>
        <Button
          color="primary"
          variant="text"
          size="small"
          endIcon={<Edit />}
          onClick={() => setIsEditMode(true)}
        >
          {title}
        </Button>
      </Tooltip>
    );
  }

  const backdropZIndex = (theme: Theme) => theme.zIndex.drawer + 1;
  const datePickerZIndex = (theme: Theme) => theme.zIndex.drawer + 2;

  return (
    <>
      <Backdrop
        open={isEditMode}
        onClick={() => setIsEditMode(false)}
        sx={{
          zIndex: backdropZIndex,
          background: 'transparent',
        }}
      />
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="center"
        sx={{ position: 'relative', zIndex: isEditMode ? datePickerZIndex : 1 }}
      >
        <CalendarTodayRoundedIcon
          fontSize="small"
          sx={{
            color: 'text.secondary',
          }}
        />
        {renderDate()}
      </Stack>
    </>
  );
}
